import { Dropdown } from '@approvalmax/ui';
import { Divider, Grid } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import moment from 'moment/moment';
import { memo, useCallback, useMemo } from 'react';
import { PaymentDetailsAudit, PaymentDetailsBankAccountInfo, PaymentDetailsStatus } from 'shared/components';
import { noValue } from 'shared/constants';
import { useGetXeroCounterpartyPaymentDetails } from 'shared/data/webApp/v1';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { InfoIcon } from './components/InfoIcon/InfoIcon';
import { DropdownActivatorRoot, Gradient, IconButton, Panel } from './PaymentDetailsIndicator.styles';
import { PaymentDetailsIndicatorProps } from './PaymentDetailsIndicator.types';

export const PaymentDetailsIndicator = memo<PaymentDetailsIndicatorProps>((props) => {
    const {
        children,
        forDropDown,
        status,
        paymentDetails: partialPaymentDetails,
        counterpartyId,
        paymentDetailsDiscrepancy,
    } = props;

    const [isOpen, toogleIsOpen] = hooks.useToggle(false);
    const company = useCompanyBasedOnUrl();

    const hasOnlyPartialPaymentDetails = !!partialPaymentDetails?.id && !partialPaymentDetails?.bankAccount.currency;

    const { data } = useGetXeroCounterpartyPaymentDetails(
        {
            path: {
                companyId: company.id,
                counterpartyId,
            },
        },
        {
            enabled: hasOnlyPartialPaymentDetails && isOpen,
            staleTime: 60 * 1000,
        }
    );

    const fullPaymentDetails = useMemo(
        () => data?.items.find((item) => item.id === partialPaymentDetails?.id),
        [partialPaymentDetails, data]
    );

    const handleOpen = useCallback(() => {
        if (!partialPaymentDetails) return;

        toogleIsOpen();
    }, [partialPaymentDetails, toogleIsOpen]);

    const handleClose = useCallback(() => {
        toogleIsOpen(false);
    }, [toogleIsOpen]);

    const paymentDetailsAudit = fullPaymentDetails || partialPaymentDetails;

    return (
        <Dropdown
            button={(ref) => (
                <DropdownActivatorRoot ref={ref}>
                    {forDropDown && <Gradient />}

                    <IconButton $forDropDown={forDropDown} disabled={!partialPaymentDetails} execute={handleOpen}>
                        <InfoIcon status={status} noPaymentDetails={!partialPaymentDetails} />
                    </IconButton>

                    {children}
                </DropdownActivatorRoot>
            )}
            isOpen={isOpen}
            onRequestClose={handleClose}
        >
            <Panel $fullWidth>
                <Grid gap={12}>
                    {status && <PaymentDetailsStatus status={status} />}

                    <PaymentDetailsBankAccountInfo
                        bankAccount={fullPaymentDetails?.bankAccount || partialPaymentDetails?.bankAccount}
                        status={status}
                        lastUsedPaymentDetailsProperties={
                            paymentDetailsDiscrepancy?.paymentDetailsDiscrepancy.lastUsedPaymentDetailsProperties
                        }
                    />

                    <Divider />

                    <PaymentDetailsAudit
                        createdAt={
                            paymentDetailsAudit?.createdAt
                                ? moment(paymentDetailsAudit.createdAt).format('lll')
                                : noValue
                        }
                        createdBy={paymentDetailsAudit?.createdBy ?? noValue}
                        modifiedAt={
                            paymentDetailsAudit?.modifiedAt
                                ? moment(paymentDetailsAudit.modifiedAt).format('lll')
                                : noValue
                        }
                        modifiedBy={paymentDetailsAudit?.modifiedBy}
                    />
                </Grid>
            </Panel>
        </Dropdown>
    );
});

PaymentDetailsIndicator.displayName = 'PaymentDetailsIndicator';
